import { useCallback } from 'react';
import { deductionsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Deduction } from '../types';

export function useDeductions(
  options?: ResourceQueryOptions
): ResourceState<Deduction> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      deductionsService.getAll(opts) as unknown as Promise<{ data: Deduction[]; meta?: any }>,
    []
  );
  return useResourceCollection<Deduction>(fetchFn, options);
}

export function useDeduction(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Deduction> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (deductionId: number, opts?: ResourceQueryOptions) =>
      deductionsService.getById(deductionId, opts) as unknown as Promise<Deduction>,
    []
  );
  return useResourceById<Deduction>(fetchFn, numericId, options);
}

export function useCreateDeduction(
  onSuccess?: (data: Deduction) => void
): MutationState<Deduction> {
  const createFn = useCallback(
    (data: any) => deductionsService.create(data) as unknown as Promise<Deduction>,
    []
  );
  return useCreateResource<Deduction>(createFn, onSuccess);
}

export function useUpdateDeduction(
  onSuccess?: (data: Deduction) => void
): MutationState<Deduction> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return deductionsService.update(numericId, data) as unknown as Promise<Deduction>;
    },
    []
  );
  return useUpdateResource<Deduction>(updateFn, onSuccess);
}

/**
 * Delete deduction using documentId
 * @param data - Full deduction object containing documentId
 */
export function useDeleteDeduction(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return deductionsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

